/**
 * Lead Store Context
 * 
 * Persistent store for tracking leads through their lifecycle.
 * Uses database for persistence, with optimistic updates for responsiveness.
 * 
 * This is the single source of truth for all lead lifecycle state.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import {
  type LifecycleLead,
  type LeadStatus,
  createLifecycleLead,
  startRouting,
  markRouted,
  markUnsold,
  queueRetry,
  startRetryRouting,
  stockLead,
  archiveLead,
  DEFAULT_RETRY_DELAY_MS,
  canBeRouted,
} from "@/lib/leadStateMachine";
import {
  useLeads,
  useLeadStats,
  useRetryReadyLeads,
  useCreateLead,
  useUpdateLead,
  useDeleteAllLeads,
  leadsQueryKeys,
} from "@/hooks/useLeadsDatabase";
import { useQueryClient } from "@tanstack/react-query";

// --- Types ---

interface LeadStats {
  total: number;
  byStatus: Record<LeadStatus, number>;
  retriesQueued: number;
  stockedCount: number;
}

interface LeadStoreActions {
  // Lead creation
  createLead: (incoming: {
    id: string;
    leadTypeId: string;
    leadTypeName: string;
    vertical: 'life_insurance' | 'mortgage';
    state: string;
    estimatedValue: number;
  }, options?: { maxRetries?: number }) => LifecycleLead;

  // State transitions
  beginRouting: (leadId: string, pricingSnapshot?: LifecycleLead['pricingSnapshot']) => LifecycleLead | null;
  completeRouting: (leadId: string, agentId: string, agentName: string, chargedAmount: number, contractId?: string) => LifecycleLead | null;
  failRouting: (leadId: string) => LifecycleLead | null;
  enqueueRetry: (leadId: string, delayMs?: number) => LifecycleLead | null;
  beginRetryRouting: (leadId: string, pricingSnapshot?: LifecycleLead['pricingSnapshot']) => LifecycleLead | null;
  moveToStock: (leadId: string) => LifecycleLead | null;
  archive: (leadId: string) => LifecycleLead | null;

  // Queries
  getLead: (leadId: string) => LifecycleLead | undefined;
  getLeadsByStatus: (status: LeadStatus) => LifecycleLead[];
  getRetryReadyLeads: () => LifecycleLead[];
  getRoutableLeads: () => LifecycleLead[];
  getStats: () => LeadStats;

  // Bulk operations
  processRetryQueue: () => string[];
  stockExhaustedLeads: () => string[];
  
  // Config
  setRetryQueueEnabled: (enabled: boolean) => void;
  
  // Utility
  clearAllLeads: () => void;
}

interface LeadStoreState {
  leads: Map<string, LifecycleLead>;
  retryQueueEnabled: boolean;
  isLoading: boolean;
}

// --- Context ---

const LeadStoreContext = createContext<{
  state: LeadStoreState;
  actions: LeadStoreActions;
} | undefined>(undefined);

// --- Provider ---

export const LeadStoreProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  
  // Database queries
  const { data: leadsData = [], isLoading } = useLeads();
  const { data: statsData } = useLeadStats();
  const { data: retryReadyData = [] } = useRetryReadyLeads();
  
  // Database mutations
  const createLeadMutation = useCreateLead();
  const updateLeadMutation = useUpdateLead();
  const deleteAllMutation = useDeleteAllLeads();
  
  // Convert array to Map for compatibility with existing code
  const leadsMap = new Map(leadsData.map(lead => [lead.id, lead]));
  
  // --- Actions ---

  const createLeadAction = useCallback((
    incoming: {
      id: string;
      leadTypeId: string;
      leadTypeName: string;
      vertical: 'life_insurance' | 'mortgage';
      state: string;
      estimatedValue: number;
    },
    options?: { maxRetries?: number }
  ): LifecycleLead => {
    const lead = createLifecycleLead(incoming, options);
    
    // Persist to database (fire and forget for speed)
    createLeadMutation.mutate(lead);
    
    // Optimistic update
    queryClient.setQueryData(leadsQueryKeys.all, (old: LifecycleLead[] = []) => [lead, ...old]);
    
    return lead;
  }, [createLeadMutation, queryClient]);

  const updateLeadInDb = useCallback((lead: LifecycleLead) => {
    updateLeadMutation.mutate(lead);
    
    // Optimistic update
    queryClient.setQueryData(leadsQueryKeys.all, (old: LifecycleLead[] = []) => 
      old.map(l => l.id === lead.id ? lead : l)
    );
    
    return lead;
  }, [updateLeadMutation, queryClient]);

  const beginRouting = useCallback((
    leadId: string,
    pricingSnapshot?: LifecycleLead['pricingSnapshot']
  ): LifecycleLead | null => {
    const lead = leadsMap.get(leadId);
    if (!lead) {
      console.error(`Lead not found: ${leadId}`);
      return null;
    }
    
    const updated = startRouting(lead, pricingSnapshot);
    return updateLeadInDb(updated);
  }, [leadsMap, updateLeadInDb]);

  const completeRouting = useCallback((
    leadId: string,
    agentId: string,
    agentName: string,
    chargedAmount: number,
    contractId?: string
  ): LifecycleLead | null => {
    const lead = leadsMap.get(leadId);
    if (!lead) {
      console.error(`Lead not found: ${leadId}`);
      return null;
    }
    
    const updated = markRouted(lead, agentId, agentName, chargedAmount, contractId);
    return updateLeadInDb(updated);
  }, [leadsMap, updateLeadInDb]);

  const failRouting = useCallback((leadId: string): LifecycleLead | null => {
    const lead = leadsMap.get(leadId);
    if (!lead) {
      console.error(`Lead not found: ${leadId}`);
      return null;
    }
    
    const updated = markUnsold(lead);
    return updateLeadInDb(updated);
  }, [leadsMap, updateLeadInDb]);

  const enqueueRetry = useCallback((
    leadId: string,
    delayMs: number = DEFAULT_RETRY_DELAY_MS
  ): LifecycleLead | null => {
    const lead = leadsMap.get(leadId);
    if (!lead) {
      console.error(`Lead not found: ${leadId}`);
      return null;
    }
    
    const updated = queueRetry(lead, delayMs);
    return updateLeadInDb(updated);
  }, [leadsMap, updateLeadInDb]);

  const beginRetryRouting = useCallback((
    leadId: string,
    pricingSnapshot?: LifecycleLead['pricingSnapshot']
  ): LifecycleLead | null => {
    const lead = leadsMap.get(leadId);
    if (!lead) {
      console.error(`Lead not found: ${leadId}`);
      return null;
    }
    
    const updated = startRetryRouting(lead, pricingSnapshot);
    return updateLeadInDb(updated);
  }, [leadsMap, updateLeadInDb]);

  const moveToStock = useCallback((leadId: string): LifecycleLead | null => {
    const lead = leadsMap.get(leadId);
    if (!lead) {
      console.error(`Lead not found: ${leadId}`);
      return null;
    }
    
    const updated = stockLead(lead);
    return updateLeadInDb(updated);
  }, [leadsMap, updateLeadInDb]);

  const archive = useCallback((leadId: string): LifecycleLead | null => {
    const lead = leadsMap.get(leadId);
    if (!lead) {
      console.error(`Lead not found: ${leadId}`);
      return null;
    }
    
    const updated = archiveLead(lead);
    return updateLeadInDb(updated);
  }, [leadsMap, updateLeadInDb]);

  // --- Queries ---

  const getLead = useCallback((leadId: string): LifecycleLead | undefined => {
    return leadsMap.get(leadId);
  }, [leadsMap]);

  const getLeadsByStatus = useCallback((status: LeadStatus): LifecycleLead[] => {
    return leadsData.filter(l => l.status === status);
  }, [leadsData]);

  const getRetryReadyLeads = useCallback((): LifecycleLead[] => {
    return retryReadyData;
  }, [retryReadyData]);

  const getRoutableLeads = useCallback((): LifecycleLead[] => {
    return leadsData.filter(canBeRouted);
  }, [leadsData]);

  const getStats = useCallback((): LeadStats => {
    if (statsData) return statsData;
    
    // Fallback to calculating from loaded data
    const byStatus: Record<LeadStatus, number> = {
      NEW: 0,
      ROUTING_IN_PROGRESS: 0,
      ROUTED: 0,
      UNSOLD: 0,
      RETRY_QUEUED: 0,
      STOCKED: 0,
      ARCHIVED: 0,
      REVIEW_REQUIRED: 0,
      CREDITED: 0,
    };

    for (const lead of leadsData) {
      byStatus[lead.status]++;
    }

    return {
      total: leadsData.length,
      byStatus,
      retriesQueued: byStatus.RETRY_QUEUED,
      stockedCount: byStatus.STOCKED,
    };
  }, [leadsData, statsData]);

  // --- Bulk Operations ---

  const processRetryQueue = useCallback((): string[] => {
    return retryReadyData.map(l => l.id);
  }, [retryReadyData]);

  const stockExhaustedLeads = useCallback((): string[] => {
    const exhausted = leadsData.filter(
      l => l.status === "UNSOLD" && l.retryCount >= l.maxRetries
    );
    
    const ids: string[] = [];
    for (const lead of exhausted) {
      const updated = stockLead(lead);
      updateLeadInDb(updated);
      ids.push(lead.id);
    }
    
    return ids;
  }, [leadsData, updateLeadInDb]);

  const clearAllLeads = useCallback(() => {
    deleteAllMutation.mutate();
  }, [deleteAllMutation]);

  const setRetryQueueEnabled = useCallback((enabled: boolean) => {
    // This is now a no-op since retry queue is handled by database queries
    console.log(`[LeadStore] Retry queue enabled: ${enabled}`);
  }, []);

  const actions: LeadStoreActions = {
    createLead: createLeadAction,
    beginRouting,
    completeRouting,
    failRouting,
    enqueueRetry,
    beginRetryRouting,
    moveToStock,
    archive,
    getLead,
    getLeadsByStatus,
    getRetryReadyLeads,
    getRoutableLeads,
    getStats,
    processRetryQueue,
    stockExhaustedLeads,
    setRetryQueueEnabled,
    clearAllLeads,
  };

  const state: LeadStoreState = {
    leads: leadsMap,
    retryQueueEnabled: true,
    isLoading,
  };

  return (
    <LeadStoreContext.Provider value={{ state, actions }}>
      {children}
    </LeadStoreContext.Provider>
  );
};

// --- Hook ---

export const useLeadStore = () => {
  const context = useContext(LeadStoreContext);
  if (context === undefined) {
    throw new Error("useLeadStore must be used within a LeadStoreProvider");
  }
  return context;
};

export default LeadStoreContext;
